import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { useDeviceLocation } from '../../hooks/useDeviceLocation';

interface DeviceLocationCenterProps {
  /**
   * False when the map already has real position context — a home base, a
   * placed waypoint, a live drone. That context wins, so no prompt is raised
   * and the view is left where it is.
   */
  enabled: boolean;
}

/**
 * Re-centers the map on the device's location, once, shortly after mount
 * (issue #401).
 *
 * The map renders immediately at its default center; this only moves it if and
 * when geolocation resolves, so the permission prompt never blocks the view.
 * Renders nothing — must live inside a `MapContainer`.
 */
export const DeviceLocationCenter: React.FC<DeviceLocationCenterProps> = ({
  enabled,
}) => {
  const map = useMap();
  const position = useDeviceLocation(enabled);
  const centered = useRef(false);

  useEffect(() => {
    // Only ever the *initial* center: once applied, or once the map has
    // position context of its own, the operator's view is theirs to keep.
    if (!enabled || position === null || centered.current) {
      return;
    }
    centered.current = true;
    // No `animate` override: Leaflet then eases short hops and jumps long
    // ones. Forcing animation would drag the view across a continent.
    map.setView(position, map.getZoom());
  }, [enabled, position, map]);

  return null;
};
